import { Plural, Trans } from "@lingui/react/macro";
import { DateTime } from "luxon";

import { currencyFormatter } from "@/common/services/formatter";
import {
  ContractStatus,
  CropUnit,
} from "@/generated/digitalnisklady.cz/graphql";
import { getUnit } from "@/routes/shared/utils/crop-unit";

type Props = {
  crop: {
    unit: CropUnit;
    name: string;
  };
  id: string;
  amount: number;
  currency: "EUR" | "CZK";
  storage: {
    name: string;
  };
  finalPrice: number;
  timeCreated: DateTime;
  className?: string;
  status?: React.ReactNode;
  externalNumber?: React.ReactNode;
};

/**
 * This one is tricky, but an `<a />` element is expected as a child. The component itself can't be wrapped in such element,
 * because of the trash bin button we have here.
 */
const ContractItem = (props: React.PropsWithChildren<Props>) => {
  return (
    <li
      className={`shadow-can-light-box relative flex min-h-[100px] cursor-pointer rounded-2xl px-4 py-3 [&_a]:absolute [&_a]:inset-0 ${props.className ?? ""}`}
      key={props.id}
    >
      <div className="flex w-full flex-col gap-y-2">
        <div className="flex flex-nowrap justify-between gap-2">
          <h3 className="text-can-midnight-steel items-center flex gap-2 font-bold">
            {currencyFormatter(props.currency).format(props.finalPrice)}/
            {getUnit(props.crop.unit)}
            {props.externalNumber}
          </h3>
          {props.status}
        </div>
        {props.children}
        <p className="flex gap-1 text-xs">
          <span>
            {props.timeCreated.toLocaleString(DateTime.DATETIME_SHORT)}
          </span>
          <span aria-hidden>|</span>
          <span>
            <Plural
              _0="-"
              few="# tuny"
              one="# tuna"
              other="# tun"
              two="# tuny"
              value={props.amount}
            />
          </span>
        </p>
      </div>
    </li>
  );
};

const Status = ({ status }: { status: ContractStatus }) => {
  let item: { gradient: string; text: React.ReactNode } = {} as typeof item;

  switch (status) {
    case ContractStatus.Failed: {
      item = {
        // eslint-disable-next-line lingui/no-unlocalized-strings
        gradient: "from-[#F99B87] to-[#E55743]",

        text: <Trans context="contract status">Odmítnuto</Trans>,
      };
      break;
    }
    case ContractStatus.Pending: {
      item = {
        // eslint-disable-next-line lingui/no-unlocalized-strings
        gradient: "from-can-tranquil-azure to-can-mystic-aqua",

        text: <Trans context="contract status">Čeká na vyřízení</Trans>,
      };
      break;
    }
    case ContractStatus.Processing: {
      item = {
        // eslint-disable-next-line lingui/no-unlocalized-strings
        gradient: "from-can-tranquil-azure to-can-mystic-aqua",

        text: <Trans context="contract status">Zpracovává se</Trans>,
      };
      break;
    }
    case ContractStatus.Success: {
      item = {
        // eslint-disable-next-line lingui/no-unlocalized-strings
        gradient: "from-[#84D4A3] to-[#51B678]",

        text: <Trans context="contract status">Vyřízeno</Trans>,
      };
      break;
    }
    case ContractStatus.Confirmed: {
      item = {
        // eslint-disable-next-line lingui/no-unlocalized-strings
        gradient: "from-[#FFC684] to-[#FF914D]",

        text: <Trans context="contract status">Připraveno k podpisu</Trans>,
      };
      break;
    }
    case ContractStatus.Signed: {
      item = {
        // eslint-disable-next-line lingui/no-unlocalized-strings
        gradient: "from-[#84D4A3] to-[#51B678]",

        text: <Trans context="contract status">Podepsáno</Trans>,
      };
      break;
    }
    default: {
      const exhaustiveCheck: never = status;
      throw new Error(`Unhandled status case: ${exhaustiveCheck}`);
    }
  }

  return (
    <small
      className={`rounded-sm bg-linear-to-r ${item.gradient} p-1.5 text-right text-xs font-normal text-white`}
    >
      {item.text}
    </small>
  );
};

const ExternalNumber = ({ children }: React.PropsWithChildren) => {
  return (
    <span className="text-xs text-can-slate-blue-gray font-normal">
      {children}
    </span>
  );
};

ContractItem.Status = Status;
ContractItem.ExternalNumber = ExternalNumber;

export { ContractItem };
